import { AbilityBuilder, ConditionsMatcher, PureAbility } from '@casl/ability';
import type { AppAbility, PermissionContext, User, UserRoleMapping } from '../types/ability.types';
import { BOQRoles, OrgRoles, ProjRoles, SiteRoles, VCRoles } from '../enums/roles';
import { Actions } from '../enums/actions';
import { Subjects } from '../enums/subjects';

// Represents the `can` function extracted from an AbilityBuilder instance.
// Uses a simplified signature that covers all call patterns used in this file.
type GrantFn = (
  action: Actions | Actions[],
  subject: Subjects,
  conditions?: Record<string, unknown>,
) => void;

const conditionsMatcher: ConditionsMatcher<Record<string, unknown>> = (conditions) => {
  return (object) => {
    if (!object || typeof object !== 'object') return false;

    const obj = object as Record<string, unknown>;

    return Object.keys(conditions).every((key) => obj[key] === conditions[key]);
  };
};

/**
 * The provisional-budget request kinds a Procurement Officer decides, one per row of PE-912's
 * routing table that names SITE_PROC_OFF and PROJ_PROC_OFF (`ProvisionalBudgetApprovalRouting`).
 * Each decide grant carries `kind`, so a later kind with other approvers (M3's amount increase,
 * PROJ_MNGR or ORG_FO) adds grants rather than reshaping these (DL-67). A kind joins this list
 * when its milestone ships the decision, not before: Allocation is M2's.
 *
 * Context: CREATE stays kindless. The matcher ignores keys a rule does not name, so the kindless
 * create grants already answer a raise check that carries `kind: 'Allocation'`, and a `kind` on
 * them would refuse the create check, which passes none (G18).
 */
const PROCUREMENT_DECIDED_KINDS = ['Create', 'Allocation'] as const;

// -------------------------------------------------------------------------
// Role-based grant helpers — each function owns the complexity of one role.
// -------------------------------------------------------------------------

/**
 * Base grants shared by ORG_OWN, ORG_ADMIN, BOQ_ADMIN and BOQ_OWN.
 *
 * Organisation Owner is a read-only financial/reporting observer — they can view
 * everything but cannot create, edit or manage any resources. All four roles
 * share these read-access grants; write capabilities are added by the specific
 * role helpers below.
 */
const grantOrgOwner = (can: GrantFn): void => {
  // Organisation
  can([Actions.READ, Actions.LIST], Subjects.ORGANISATION);
  can(Actions.READ, Subjects.ORG_USER);
  // Projects
  can([Actions.READ, Actions.LIST], Subjects.PROJECT);
  // Sites
  can([Actions.READ, Actions.LIST], Subjects.SITE);
  // Vendors & Items (read/list only — no create/edit/delete)
  can([Actions.READ, Actions.LIST], Subjects.ORG_VENDOR);
  can([Actions.READ, Actions.LIST], Subjects.BOQ_VENDOR);
  can([Actions.READ, Actions.LIST], Subjects.ORG_VENDOR_ITEM);
  // BOQ (view + export; no write access)
  can([Actions.READ, Actions.EXPORT], Subjects.BOQ);
  can(Actions.READ, Subjects.SELLING_BOQ);
  can(Actions.READ, Subjects.BOQ_TEMPLATE);
  can([Actions.READ, Actions.LIST], Subjects.CLIENT_VARIATION);
  // Context: the server also refuses an org-wide reader who is a site or project professional on
  // the bill. Grants cannot express that without cannot(), so such a user may see the entry
  // point and is refused by the server, which is authoritative (DL-142).
  can(Actions.READ, Subjects.PROVISIONAL_BUDGET);
  // Orders (view + export + share; OO/BO cannot create, approve or cancel)
  can([Actions.READ, Actions.LIST, Actions.EXPORT, Actions.SHARE], Subjects.ORDER);
  // Context: bulk orders are read-only for the whole org tier. Only the PROJ_PROC_OFF
  // capability may initiate one (FR-45), so CREATE is withheld here even from ORG_ADMIN and
  // BOQ_ADMIN, which hold MANAGE on ordinary orders. Read arrives through the same
  // cross-organisation tier the Core API calls FullOrgViewAccess(), which is also how back
  // office reaches the capability rather than through a bulk-specific role (PE-608 DL-50/51).
  can([Actions.READ, Actions.LIST], Subjects.BULK_ORDER);
  // Payments, PRs & APs (view only)
  can([Actions.READ, Actions.LIST], Subjects.PAYMENT_REQUEST);
  can([Actions.READ, Actions.LIST], Subjects.ACCOUNTS_PAYABLE);
  can([Actions.READ, Actions.LIST], Subjects.PAYMENT);
  // Context: the payments surface is the API's DL-119 view rule, the org finance tier only.
  // This helper and grantOrgFinanceBase are the only places it is granted, so no project or
  // site role reaches it (DL-152).
  can(Actions.LIST, Subjects.PAYMENTS_SURFACE);
  // Compliance (view + download; no upload/delete)
  can(Actions.READ, Subjects.COMPLIANCE);
  can(Actions.READ, Subjects.COMPLIANCE_TEMPLATE);
  can(Actions.DOWNLOAD, Subjects.COMPLIANCE_DOCUMENT);
  // Dashboards
  can(Actions.READ, Subjects.ORG_DASHBOARD); // View Org Dashboard page (content access)
  can(Actions.LIST, Subjects.ORG_DASHBOARD); // Show Dashboard nav tab
  // Cashflow (view + export; no overhead updates)
  can([Actions.READ, Actions.EXPORT], Subjects.PROJECT_CASHFLOW);
  can([Actions.READ, Actions.EXPORT], Subjects.SITE_CASHFLOW);
  // Bank draws — view only. Owners see everything and change nothing, so no UPDATE and no
  // claim generation (PLE-1236 A3).
  can([Actions.READ, Actions.LIST], Subjects.BANK_DRAW);
  can(Actions.READ, Subjects.CLAIM_SHEET);
  // Budget summary cards/tiles — distinct from raw cashflow data
  can(Actions.READ, Subjects.PROJECT_BUDGET_SUMMARY_CARD); // Projects section "View Project Budget Summaries"
  can(Actions.READ, Subjects.SITE_PROJECT_BUDGET_SUMMARY_TILES); // Sites section "View Project Budget Summary"
  // Returns (view only)
  can([Actions.READ, Actions.LIST], Subjects.RETURN);
  // Credits & Refunds (view only)
  can([Actions.READ, Actions.LIST], Subjects.CREDIT);
  can([Actions.READ, Actions.LIST], Subjects.REFUND);
};

/**
 * Additional grants for ORG_ADMIN (stacked on top of grantOrgOwner).
 * BOQ_ADMIN receives everything here plus grantBoqAdminExtras.
 *
 * Notable differences from ORG_OWNER:
 * - Can create/edit/delete projects, sites, vendors, BOQs
 * - Can manage carts, approve orders, log deliveries
 * - Can approve/reject PRs and log payments
 * - Can manage returns and complete credits/refunds
 * - Cannot publish BOQ templates (BOQ_ADMIN only) or create organisations (BOQ_ADMIN only)
 */
const grantOrgAdmin = (can: GrantFn): void => {
  // Organisation
  can(Actions.UPDATE, Subjects.ORGANISATION);
  can(Actions.MANAGE, Subjects.ORG_USER);
  // Projects (full CRUD + user management)
  can(Actions.MANAGE, Subjects.PROJECT);
  can(Actions.MANAGE, Subjects.PROJECT_USER);
  // Sites (full CRUD + user management + date changes)
  can(Actions.MANAGE, Subjects.SITE);
  can(Actions.MANAGE, Subjects.SITE_USER);
  // Vendors (create/edit/delete org vendors + sync BOQ vendors)
  can(Actions.MANAGE, Subjects.ORG_VENDOR);
  can(Actions.SYNC, Subjects.BOQ_VENDOR);
  can(Actions.MANAGE, Subjects.ORG_VENDOR_ITEM);
  // BOQ (full management)
  can(Actions.MANAGE, Subjects.BOQ);
  // BOQ Template: create + delete only — PUBLISH is BOQ_ADMIN-exclusive
  can([Actions.CREATE, Actions.DELETE], Subjects.BOQ_TEMPLATE);
  can(Actions.MANAGE, Subjects.BOQ_ITEM);
  can(Actions.MANAGE, Subjects.BOQ_VARIATION);
  can([Actions.CREATE, Actions.UPDATE, Actions.CANCEL], Subjects.CLIENT_VARIATION);
  can(Actions.CREATE, Subjects.CLIENT_VARIATION_PAYMENT);
  // Context: create only, never MANAGE. Approving or declining a provisional budget stays with
  // the Procurement Officers, and the server's approver check has no administrator short-circuit
  // (V5-19).
  can(Actions.CREATE, Subjects.PROVISIONAL_BUDGET);
  // Orders (full management: create, approve, cancel, log delivery)
  can(Actions.MANAGE, Subjects.CART);
  can(Actions.MANAGE, Subjects.ORDER);
  can(Actions.MANAGE, Subjects.ORDER_VARIATION);
  can([Actions.UPLOAD, Actions.DELETE], Subjects.ORDER_DOCUMENTATION);
  can(Actions.CREATE, Subjects.ORDER_DELIVERY);
  // Payments, PRs & APs.
  // ORG_ADMIN is view-only for the financial domain — it keeps only READ/LIST on PRs and full
  // AP/payment viewing, and has NO PR write actions (CREATE/UPDATE/APPROVE/DECLINE/CANCEL), no
  // PR document management, and no adhoc (ADV) payment creation (PE-214, mirroring BE PE-212).
  // This aligns the factory with the live legacy adapter, which grants ORG_ADMIN no
  // CREATE/CANCEL on PRs at all. (PRs are never manually created — they are generated when an
  // order is approved — so CREATE is moot.) READ/LIST:PAYMENT_REQUEST also come from
  // grantOrgOwner (the read-only base). VERIFY is never granted (SITE_FIN_OFF exclusive).
  can([Actions.READ, Actions.LIST], Subjects.PAYMENT_REQUEST);
  can(Actions.MANAGE, Subjects.ACCOUNTS_PAYABLE);
  // Context: no CREATE:PAYMENT. BE #1375 narrowed PaymentPermissionService.CanCreate from
  // {BOQ_ADMIN, ORG_ADMIN, ORG_FO, ORG_FA} to {ORG_FO, ORG_FA} on 14 September — "logging a
  // payment releases funds, so it is restricted to the org finance officers". This grant was
  // correct until that landed; it supersedes BE DP2.
  can([Actions.CREATE, Actions.SHARE, Actions.DELETE], Subjects.AP_STATEMENT);
  // Compliance (upload + delete documents)
  can([Actions.UPLOAD, Actions.DELETE], Subjects.COMPLIANCE_DOCUMENT);
  // Dashboards (change financial year)
  can(Actions.UPDATE, Subjects.ORG_DASHBOARD);
  // Cashflow (update overheads)
  can(Actions.UPDATE, Subjects.PROJECT_CASHFLOW);
  can(Actions.UPDATE, Subjects.SITE_CASHFLOW);
  // Bank draws — full: view, fill in the amounts, set the schedule, generate claim sheets.
  can([Actions.READ, Actions.LIST, Actions.UPDATE], Subjects.BANK_DRAW);
  can([Actions.READ, Actions.CREATE], Subjects.CLAIM_SHEET);
  can(Actions.UPDATE, Subjects.DRAW_SCHEDULE);
  // Returns (create, approve, reject, cancel)
  can([Actions.CREATE, Actions.APPROVE, Actions.DECLINE, Actions.CANCEL], Subjects.RETURN);
  // Credits & Refunds.
  // ORG_ADMIN has no credit cancel/complete (PE-460, mirroring BE PE-461). BOQ_ADMIN also runs
  // grantOrgAdmin and is depowered here too (grantBoqAdminExtras does not re-grant credit).
  // Dual-role ORG_FO/ORG_FA keep credit via grantOrgFinanceBase (grant absence, never cannot()).
};

/**
 * ORG_ADMIN grants that BOQ_ADMIN does not share, although it runs grantOrgAdmin.
 */
const grantOrgAdminOnly = (can: GrantFn): void => {
  // Context: approving a bulk order is the approve arm of an ordinary order's, minus its site
  // role (DL-154, DL-185 A-15), and without BOQ_ADMIN, which the platform is moving off
  // approvals (DL-225). Declining, cancelling, logging a delivery and completing take the same
  // grant (PE-1033, PE-1049, PE-1054), and so do sharing it and marking its vendor notified
  // (DL-260): a plain reader may download the vendor PO but never email the vendor.
  can(
    [
      Actions.APPROVE,
      Actions.DECLINE,
      Actions.CANCEL,
      Actions.LOG_DELIVERY,
      Actions.COMPLETE,
      Actions.SHARE,
      Actions.UPDATE,
    ],
    Subjects.BULK_ORDER,
  );
};

/**
 * BOQ_ADMIN-exclusive capabilities — applied on top of grantOrgOwner + grantOrgAdmin.
 * These are platform-level administrative powers not available to ORG_ADMIN.
 */
const grantBoqAdminExtras = (can: GrantFn): void => {
  // Platform: can create new organisations
  can(Actions.CREATE, Subjects.ORGANISATION);
  // BOQ Vendor full management (ORG_ADMIN can only sync; BOQ_ADMIN can create/edit/delete)
  can(Actions.MANAGE, Subjects.BOQ_VENDOR);
  // BOQ Vendor Items full management (ORG_ADMIN has no access to BOQ items)
  can(Actions.MANAGE, Subjects.BOQ_VENDOR_ITEM);
  // BOQ Template: publish / make public (ORG_ADMIN can only create/delete)
  can(Actions.PUBLISH, Subjects.BOQ_TEMPLATE);
  // Compliance templates (upload + delete — ORG_ADMIN cannot)
  can([Actions.UPLOAD, Actions.DELETE], Subjects.COMPLIANCE_TEMPLATE);
  // PR write actions + adhoc payment: BOQ_ADMIN borrows grantOrgAdmin, which grants ORG_ADMIN
  // no PR write actions (PE-214). PE-460 depowers BOQ_ADMIN too (mirroring BE PE-461), so it
  // follows grantOrgAdmin and holds no PR write / PR docs / adhoc creation. AP_STATEMENT stays
  // (granted unconditionally by grantOrgAdmin). CREATE:PAYMENT no longer does — BE #1375 took
  // both BOQ_ADMIN and ORG_ADMIN off it on 14 September, superseding BE DP2.
};

/**
 * Shared base grants for both ORG_FO and ORG_FA.
 *
 * Both finance roles have identical read/navigation access and identical
 * write capabilities for AP, payments, PR metadata, cashflow overheads,
 * and credits/refunds. The only differences are applied by the role-specific
 * helpers on top of this base:
 *
 *  - ORG_FO adds: UPDATE:ORGANISATION, APPROVE+PUSH_BACK:PAYMENT_REQUEST, UPDATE:ORG_DASHBOARD,
 *    APPROVE+PUSH_BACK:BULK_PAYMENT_REQUEST
 *  - ORG_FA adds: DECLINE+VERIFY:BULK_PAYMENT_REQUEST only (see grantOrgFA)
 */
const grantOrgFinanceBase = (can: GrantFn): void => {
  // Organisation (view only — UPDATE is ORG_FO exclusive)
  can([Actions.READ, Actions.LIST], Subjects.ORGANISATION);
  can(Actions.READ, Subjects.ORG_USER);

  // Projects (view only — no create/delete/manage)
  can([Actions.READ, Actions.LIST], Subjects.PROJECT);
  can(Actions.READ, Subjects.PROJECT_BUDGET_SUMMARY_CARD);

  // Sites (view only — no create/edit/delete/manage)
  can([Actions.READ, Actions.LIST], Subjects.SITE);
  can(Actions.READ, Subjects.SITE_PROJECT_BUDGET_SUMMARY_TILES);

  // Cashflow (view + export + update overheads)
  can([Actions.READ, Actions.EXPORT, Actions.UPDATE], Subjects.PROJECT_CASHFLOW);
  can([Actions.READ, Actions.EXPORT, Actions.UPDATE], Subjects.SITE_CASHFLOW);
  // Bank draws — org finance fills in the draw amounts and generates claim sheets. No
  // UPDATE:DRAW_SCHEDULE: the site's dates are not theirs, which is the whole reason the
  // schedule is its own subject (PLE-1236 A6).
  can([Actions.READ, Actions.LIST, Actions.UPDATE], Subjects.BANK_DRAW);
  can([Actions.READ, Actions.CREATE], Subjects.CLAIM_SHEET);

  // BOQ (view + export only — no create/delete/submit/approve)
  can([Actions.READ, Actions.EXPORT], Subjects.BOQ);
  can(Actions.READ, Subjects.SELLING_BOQ);
  can(Actions.READ, Subjects.BOQ_TEMPLATE);
  // Read only, like the cost BOQ. See grantOrgOwner for the professional-on-the-bill case.
  can(Actions.READ, Subjects.PROVISIONAL_BUDGET);

  // Client Variations (view only + log payment)
  can([Actions.READ, Actions.LIST], Subjects.CLIENT_VARIATION);
  can(Actions.CREATE, Subjects.CLIENT_VARIATION_PAYMENT);

  // Orders & Claims (view + export + share; no create/approve/cancel/deliver)
  can([Actions.READ, Actions.LIST, Actions.EXPORT, Actions.SHARE], Subjects.ORDER);
  // Bulk orders — read only, same org tier as grantOrgOwner. No CREATE: see the note there.
  can([Actions.READ, Actions.LIST], Subjects.BULK_ORDER);

  // Payment Requests — metadata management (READ + LIST + UPDATE)
  // APPROVE (Stage 2) and PUSH_BACK are ORG_FO exclusive and added by grantOrgFO
  // Context: no CREATE. PRs are generated when an order or claim is approved; the one manual
  // kind, an adhoc (ADV) payment, is CREATE:ADHOC_PAYMENT below.
  can([Actions.READ, Actions.LIST, Actions.UPDATE], Subjects.PAYMENT_REQUEST);
  can([Actions.UPLOAD, Actions.DELETE], Subjects.PR_DOCUMENTATION);

  // Accounts Payable — full AP tab access (unconditional, org-level)
  can([Actions.READ, Actions.LIST], Subjects.ACCOUNTS_PAYABLE);

  // Payments — list + create/log + view proof of payment
  can([Actions.READ, Actions.LIST, Actions.CREATE], Subjects.PAYMENT);
  // Adhoc (ADV) payment creation — finance roles retain it (PE-214 only depowers ORG_ADMIN)
  can(Actions.CREATE, Subjects.ADHOC_PAYMENT);
  // Context: the other half of the DL-119 view rule; see grantOrgOwner (DL-152).
  can(Actions.LIST, Subjects.PAYMENTS_SURFACE);

  // AP Statement — generate, share, delete
  can([Actions.CREATE, Actions.SHARE, Actions.DELETE], Subjects.AP_STATEMENT);

  // Compliance — view + download only (no upload/delete for either finance role)
  can(Actions.READ, Subjects.COMPLIANCE);
  can(Actions.READ, Subjects.COMPLIANCE_TEMPLATE);
  can(Actions.DOWNLOAD, Subjects.COMPLIANCE_DOCUMENT);

  // Dashboards — view only (UPDATE is ORG_FO exclusive)
  can(Actions.READ, Subjects.ORG_DASHBOARD);
  can(Actions.LIST, Subjects.ORG_DASHBOARD); // Show Dashboard nav tab

  // Returns (view only — no create/approve/cancel)
  can([Actions.READ, Actions.LIST], Subjects.RETURN);

  // Credits & Refunds — view + cancel + complete (org-level, no site condition needed)
  can([Actions.READ, Actions.LIST, Actions.CANCEL, Actions.COMPLETE], Subjects.CREDIT);
  can([Actions.READ, Actions.LIST, Actions.CANCEL, Actions.COMPLETE], Subjects.REFUND);

  // Vendor catalogue — read/list only (no create/edit/delete/sync)
  can([Actions.READ, Actions.LIST], Subjects.ORG_VENDOR);
  can([Actions.READ, Actions.LIST], Subjects.ORG_VENDOR_ITEM);
  can([Actions.READ, Actions.LIST], Subjects.BOQ_VENDOR);
  can([Actions.READ, Actions.LIST], Subjects.BOQ_VENDOR_ITEM);
};

/**
 * ORG_FO (Finance Officer) grants — V2 sheet column "Finance Officer".
 *
 * Stacks on top of grantOrgFinanceBase. ORG_FO is the sole holder of Stage 2
 * (Final Approval) on payment requests and has full org-level financial authority.
 *
 * Extras over ORG_FA:
 *  - UPDATE:ORGANISATION — can edit org settings
 *  - APPROVE:PAYMENT_REQUEST — sole Stage 2 actor (V2), which is the API's FinalApproval
 *  - UPDATE:ORG_DASHBOARD — can change the financial year
 *  - APPROVE+PUSH_BACK:BULK_PAYMENT_REQUEST — the same stage on a bulk request (PE-1178)
 */
const grantOrgFO = (can: GrantFn): void => {
  grantOrgFinanceBase(can);
  can(Actions.UPDATE, Subjects.ORGANISATION);
  // Context: APPROVE is Stage 2 and ORG_FO holds it alone — PaymentRequestManagementService
  // routes approving a Verified PR to EntityAction.FinalApproval, which is
  // CanFinalApprove() == ORG_FO. There is no DECLINE pair: declining validates whichever stage
  // the request is at, so a caller asks VERIFY on a Requested PR and APPROVE on a Verified one
  // (PLE-1376).
  can([Actions.APPROVE, Actions.PUSH_BACK], Subjects.PAYMENT_REQUEST);
  // A bulk request's final approval and push back are the same ORG_FO arm, keyed on the bulk
  // marker rather than a site (PE-1178, DL-153).
  can([Actions.APPROVE, Actions.PUSH_BACK], Subjects.BULK_PAYMENT_REQUEST);
  can(Actions.UPDATE, Subjects.ORG_DASHBOARD);
};

/**
 * ORG_FA (Finance Admin) grants — V2 sheet column "Finance Admin".
 *
 * Stacks on top of grantOrgFinanceBase. ORG_FA is a subset of ORG_FO in every grant but its
 * bulk payment request pair: it has zero approval authority and cannot edit the organisation or
 * change the financial year. All other financial capabilities are identical to ORG_FO.
 *
 * Differences from ORG_FO (absent):
 *  - No UPDATE:ORGANISATION
 *  - No APPROVE/DECLINE:PAYMENT_REQUEST
 *  - No APPROVE/PUSH_BACK:BULK_PAYMENT_REQUEST
 *  - No UPDATE:ORG_DASHBOARD
 *
 * The grants ORG_FO lacks: DECLINE and VERIFY on BULK_PAYMENT_REQUEST.
 */
const grantOrgFA = (can: GrantFn): void => {
  grantOrgFinanceBase(can);
  grantOrgFABulkPaymentRequest(can);
};

/**
 * ORG_FA's grants that ORG_FO lacks, given whenever ORG_FA is held, alongside ORG_FO too.
 *
 * Context: a bulk request has no site, so the two stages cannot scope it, and the API gives it
 * its own arm, keyed on the bulk marker: ORG_FA verifies and declines from Requested, ORG_FO
 * approves, pushes back and declines from Verified (DL-205, PE-1040, PE-1178). A lean org's one
 * finance principal holds both roles (requirements-v7 §2), and the API lets that holder act at
 * both stages, including on a request they verified themselves (DL-215).
 */
const grantOrgFABulkPaymentRequest = (can: GrantFn): void => {
  can([Actions.DECLINE, Actions.VERIFY], Subjects.BULK_PAYMENT_REQUEST);
};

/** BOQ_CAT_ADMIN: the central vendor catalogue only, in every org (PLE-1768). */
const grantCatalogAdmin = (can: GrantFn): void => {
  can(Actions.MANAGE, Subjects.BOQ_VENDOR);
  can(Actions.MANAGE, Subjects.BOQ_VENDOR_ITEM);
};

/**
 * BOQ_OWN-exclusive capabilities — applied on top of grantOrgOwner.
 */
const grantBoqOwner = (can: GrantFn): void => {
  can([Actions.LIST, Actions.READ], Subjects.BOQ_VENDOR_ITEM);
  // Bank draws — view only, like the org owner it mirrors.
  can([Actions.READ, Actions.LIST], Subjects.BANK_DRAW);
  can(Actions.READ, Subjects.CLAIM_SHEET);
};

// -------------------------------------------------------------------------
// Combined role grant functions
// -------------------------------------------------------------------------

/**
 * PROJ_PROC_OFF grants — project procurement officer.
 *
 * The role substitutes for SITE_PROC_OFF at project scope rather than adding to it, so its
 * grant set is that role's, translated from { siteId } to { projectId } (PE-571's derivation
 * rule). Lifted out of `applyProjectRoles` because it is by far the largest project-role
 * grant set — 15 Core API permission services' worth.
 *
 * Two rules govern every line below, because a frontend ability wider than its backend grant
 * is a control that fails on click:
 *  - Unconditional grants exist only where the Core API's arm is `InRoles(userId, orgId, …)`
 *    — the role held anywhere in the org. Anything the API scopes to a project stays
 *    conditional, including READ where only LIST is org-wide.
 *  - Where the API's arms match SITE_PROC_OFF's, this mirrors that role's action set plus any
 *    action the API grants that SITE_PROC_OFF's CASL block omits. Where they differ —
 *    payments, payment requests, compliance upload, vendor delete, projects, sites — the
 *    narrower side wins and the reason is recorded inline.
 */
const grantProjectProcurementOfficer = (
  can: GrantFn,
  projectsByRole: Map<ProjRoles, string[]>,
): void => {
  const projectIds = projectsByRole.get(ProjRoles.PROJ_PROC_OFF);
  if (!projectIds) return;

  // Destructured so a grant fits on one line, subject first: read each line as
  // "this subject, these actions" rather than unpacking a wrapped array to find the subject.
  const {
    READ,
    LIST,
    CREATE,
    UPDATE,
    DELETE,
    EXPORT,
    SUBMIT,
    APPROVE,
    DECLINE,
    CANCEL,
    MANAGE,
    SYNC,
    BULK_UPLOAD,
    UPLOAD,
    DOWNLOAD,
    LOG_DELIVERY,
    COMPLETE,
    SHARE,
  } = Actions;

  // A one-action grant is passed through unwrapped so the emitted rule keeps CASL's scalar
  // `action` shape rather than a one-element array — same decision, different rule object.
  const one = (actions: Actions[]): Actions | Actions[] => {
    const [first, ...rest] = actions;

    return first !== undefined && rest.length === 0 ? first : actions;
  };

  /**
  The API's arm is `InRoles(userId, orgId, …)` — the role held on any project in the org.
  */
  const orgWide = (subject: Subjects, actions: Actions[]): void => can(one(actions), subject);

  // --- Org-wide ---------------------------------------------------------------------------
  orgWide(Subjects.ORGANISATION, [READ, LIST]);
  orgWide(Subjects.ORG_USER, [READ]);
  // Vendors and items are org-level entities with no project-scoped equivalent, so the API
  // grants them role-anywhere. DELETE:ORG_VENDOR is the one place this role is wider than
  // SITE_PROC_OFF, which has no arm in VendorPermissionService.CanDelete.
  orgWide(Subjects.ORG_VENDOR, [READ, LIST, CREATE, UPDATE, DELETE]);
  orgWide(Subjects.BOQ_VENDOR, [READ, LIST, SYNC]);
  orgWide(Subjects.ORG_VENDOR_ITEM, [READ, LIST, CREATE, UPDATE, DELETE, BULK_UPLOAD]);
  // Templates — view only. Boq.CanListTemplates admits any ORG_MEMB; MakeTemplatePublic is
  // BOQ_ADMIN-exclusive and the org-level (no-site) DeleteTemplate arm stays admin-only.
  orgWide(Subjects.BOQ_TEMPLATE, [READ]);
  orgWide(Subjects.COMPLIANCE_TEMPLATE, [READ]);
  // LIST only, wherever the API's list arm is org-wide; READ stays per-project below, because
  // every single-entity View arm is scoped to the project.
  orgWide(Subjects.PROJECT, [LIST]);
  orgWide(Subjects.ORDER, [LIST]);
  orgWide(Subjects.RETURN, [LIST]);
  orgWide(Subjects.PAYMENT_REQUEST, [LIST]);
  orgWide(Subjects.CREDIT, [LIST]);
  orgWide(Subjects.REFUND, [LIST]);
  // Deliberately absent: PAYMENT, ADHOC_PAYMENT, ACCOUNTS_PAYABLE, AP_STATEMENT and both
  // dashboards. PaymentPermissionService grants this role nothing — a divergence from live
  // SITE_PROC_OFF, which can log payments org-wide, kept so bulk ordering's separation of
  // duties survives role assignment (PE-571). Every ViewStats arm is org-role only.

  for (const projectId of projectIds) {
    const onProject = (subject: Subjects, actions: Actions[]): void =>
      can(one(actions), subject, { projectId });

    // --- Structure ------------------------------------------------------------------------
    // PROJECT matches on its own id, not a projectId field — the only grant shaped this way.
    // View and finance only; Create/Update/ViewStats stay with PROJ_MNGR.
    can([READ, LIST], Subjects.PROJECT, { id: projectId });
    onProject(Subjects.PROJECT_BUDGET_SUMMARY_CARD, [READ]);
    onProject(Subjects.SITE_PROJECT_BUDGET_SUMMARY_TILES, [READ]);
    // Sites — read only, and no SITE_USER. FR-58's date extension is a side effect of order
    // placement rather than a site edit, so SitePermissionService needs no write arm here.
    onProject(Subjects.SITE, [READ, LIST]);

    // --- Cashflow -------------------------------------------------------------------------
    // Overheads are updated at site level and propagate to the project cashflow, so UPDATE
    // sits on SITE_CASHFLOW only (same shape as PROJ_MNGR).
    onProject(Subjects.PROJECT_CASHFLOW, [READ, EXPORT]);
    onProject(Subjects.SITE_CASHFLOW, [READ, UPDATE, EXPORT]);
    // Bank draws — runs the project, so full on its draws and on the schedule they hang off.
    onProject(Subjects.BANK_DRAW, [READ, LIST, UPDATE]);
    onProject(Subjects.CLAIM_SHEET, [READ, CREATE]);
    onProject(Subjects.DRAW_SCHEDULE, [UPDATE]);

    // --- BOQ ------------------------------------------------------------------------------
    // Authoring, approval, variations and claims. With PROJ_CO dropped from the V2 role set,
    // this is the only project-level role that can run claims at all, so it both edits and
    // approves them; that separation-of-duties reduction is owned by the platform role
    // decision, not by this factory (PE-571).
    onProject(Subjects.BOQ, [CREATE, READ, UPDATE, DELETE, EXPORT, SUBMIT, APPROVE, DECLINE]);
    onProject(Subjects.SELLING_BOQ, [READ]);
    onProject(Subjects.BOQ_TEMPLATE, [CREATE]);
    onProject(Subjects.BOQ_ITEM, [MANAGE]);
    onProject(Subjects.BOQ_VARIATION, [CREATE, APPROVE, DECLINE]);
    // Decides wherever SITE_PROC_OFF does on the routing table, so a site whose only
    // Procurement Officer is project-level still has an approver (DL-51, DL-137). Each decide
    // grant names its kind; a decide check without one is refused (DL-67).
    onProject(Subjects.PROVISIONAL_BUDGET, [READ, CREATE]);
    for (const kind of PROCUREMENT_DECIDED_KINDS) {
      can([APPROVE, DECLINE], Subjects.PROVISIONAL_BUDGET, { projectId, kind });
    }

    // --- Client variations ----------------------------------------------------------------
    // LogPayment is granted, unlike Payment's, because it records a client payment received
    // against a variation rather than releasing funds to a vendor.
    onProject(Subjects.CLIENT_VARIATION, [READ, LIST, CREATE, UPDATE, CANCEL]);
    onProject(Subjects.CLIENT_VARIATION_PAYMENT, [CREATE]);

    // --- Orders, cart and returns ---------------------------------------------------------
    // Full lifecycle; the API's arms match SITE_PROC_OFF's.
    onProject(Subjects.CART, [MANAGE]);
    onProject(Subjects.ORDER, [MANAGE]);
    onProject(Subjects.ORDER_VARIATION, [MANAGE]);
    onProject(Subjects.ORDER_DOCUMENTATION, [UPLOAD, DELETE]);
    onProject(Subjects.ORDER_DELIVERY, [CREATE]);
    onProject(Subjects.RETURN, [CREATE, READ, LIST, UPDATE, APPROVE, DECLINE, CANCEL]);
    // Context: a bulk order is a project-level act, so all three actions — including LIST —
    // stay per-project. ORDER's LIST is org-wide above because the Core API's list arm is;
    // PE-608's contract scopes bulk read to "own project only", and mirror-never-exceed makes
    // the narrower side win. Complete arrives in milestone B.5.
    // UPLOAD and DELETE are the parent's shared documents (PE-886): S-07 gives them to this role
    // alone, so PROJ_MNGR and the org tier see the hub's documents but cannot change them.
    // APPROVE is the bulk approve arm (DL-154, A-15), which PROJ_MNGR holds too; DECLINE and
    // CANCEL take the same grant (PE-1033), and so do LOG_DELIVERY (PE-1049), COMPLETE (PE-1054),
    // and SHARE and UPDATE, which send the vendor PO and mark the vendor notified (DL-260).
    onProject(Subjects.BULK_ORDER, [
      CREATE,
      READ,
      LIST,
      UPLOAD,
      DELETE,
      APPROVE,
      DECLINE,
      CANCEL,
      LOG_DELIVERY,
      COMPLETE,
      SHARE,
      UPDATE,
    ]);

    // --- Money (read only) ------------------------------------------------------------------
    // The role must see the PR that its own order approval produced and must never raise,
    // verify, approve, push back or pay one; Double-Lock is untouched. No PR_DOCUMENTATION at
    // all. Credit/refund review, cancel and complete are org finance.
    onProject(Subjects.PAYMENT_REQUEST, [READ, LIST]);
    onProject(Subjects.CREDIT, [READ, LIST]);
    onProject(Subjects.REFUND, [READ, LIST]);

    // --- Compliance -------------------------------------------------------------------------
    // UPLOAD and DELETE are granted here but not to SITE_PROC_OFF, which has no arm in
    // ComplianceDocumentPermissionService.CanCreate. Delete is not a separate arm:
    // EntityAction.Delete dispatches to CanCreate, so whoever may upload may also remove.
    onProject(Subjects.COMPLIANCE, [READ]);
    onProject(Subjects.COMPLIANCE_DOCUMENT, [UPLOAD, DOWNLOAD, DELETE]);
  }
};

/**
 * Applies all project-role grants for every project the user has a role in.
 *
 * Two tiers of grants are produced for each role:
 *
 * 1. **Unconditional "navigation" grants** (READ/LIST on top-level subjects) —
 *    allow UI nav items and coarse visibility checks without a specific resource:
 *      ability.can(Actions.READ, Subjects.BOQ)  // → true if role exists on any project
 *
 * 2. **Per-project conditional grants** — scoped to each individual projectId so
 *    that resource-level checks work correctly via the `subject()` helper:
 *      ability.can(Actions.MANAGE, subject(Subjects.BOQ, { projectId: 'abc' }))  // → true only for that project
 *      ability.can(Actions.MANAGE, subject(Subjects.BOQ, { projectId: 'xyz' }))  // → false
 */
const applyProjectRoles = (can: GrantFn, projects: UserRoleMapping[]): void => {
  // Build a role → projectId[] map so we can iterate per role efficiently
  const projectsByRole = new Map<ProjRoles, string[]>();
  for (const project of projects) {
    for (const role of project.roles as ProjRoles[]) {
      const existing = projectsByRole.get(role) ?? [];
      projectsByRole.set(role, [...existing, project.id]);
    }
  }

  if (projectsByRole.has(ProjRoles.PROJ_MNGR)) {
    const projectIds = projectsByRole.get(ProjRoles.PROJ_MNGR)!;

    // --- Unconditional grants  ---
    can([Actions.READ, Actions.LIST], Subjects.ORGANISATION);
    can(Actions.READ, Subjects.ORG_USER);
    // Vendor management — PM has full CRUD on org vendors + read/list/sync on BOQ vendors
    can(Actions.MANAGE, Subjects.ORG_VENDOR);
    can([Actions.READ, Actions.LIST, Actions.SYNC], Subjects.BOQ_VENDOR);
    can(Actions.MANAGE, Subjects.ORG_VENDOR_ITEM);
    // Site management. Read and list stay unconditional: the sites list and the LIST:SITE route
    // guard both ask without an id, and a type-level check ignores conditions anyway. Create and
    // update are scoped per project below.
    //
    // The note that stood here said scoping was impossible because "every caller asks
    // can(UPDATE, SITE, { siteId }), and a projectId condition can never match a siteId query".
    // That was true, and it is exactly what the Area D callers now fix — they pass
    // { siteId, projectId } together, so a { projectId } rule matches. Unconditional MANAGE was
    // an over-grant the API refused: CanUpdate admits PROJ_MNGR only via InProjectRoles
    // (PLE-1317 Q4).
    can([Actions.READ, Actions.LIST], Subjects.SITE);
    // BOQ Templates — view all (Full)
    can(Actions.READ, Subjects.BOQ_TEMPLATE);
    // Accounts Payable — PM has no AP access (Financial Permissions Cleanup, PE-213).
    can([Actions.READ, Actions.LIST], Subjects.PAYMENT);
    // Context: AP Statement is org-finance only. AccountsPayableSummaryPermissionService
    // declares empty site and project role arrays, so its ORG_MEMB fallthrough can never match
    // and the effective audience is FullOrgViewAccess() alone — a PM got a 403 (PLE-1275).
    // Compliance templates (view only, Full)
    can(Actions.READ, Subjects.COMPLIANCE_TEMPLATE);
    // Context: PROJECT stays unconditional. The Core API lets org members list projects with no
    // assigned-resource restriction; viewing one project is narrowed server-side to the PM's own
    // (catalogue/server-scoped.ts). ORDER used to sit here for the Orders/Claims nav and
    // did not need to: MANAGE:ORDER per project is granted below, and a bare can(LIST, ORDER)
    // matches that conditional rule, so the nav works without the cross-project read (PLE-1241).
    can([Actions.READ, Actions.LIST], Subjects.PROJECT);
    // Sites section "View Project Budget Summary" = Full for PM (unconditional)
    can(Actions.READ, Subjects.SITE_PROJECT_BUDGET_SUMMARY_TILES);

    // --- Per-project conditional grants ---
    for (const projectId of projectIds) {
      // Create and edit sites on projects this PM manages — CanCreate(projectId), and
      // CanUpdate(siteId)'s InProjectRoles arm (PLE-1317 Q1, Q2, Q4).
      can([Actions.CREATE, Actions.UPDATE], Subjects.SITE, { projectId });
      // Project management (edit + delete scoped to own project)
      can([Actions.UPDATE, Actions.DELETE], Subjects.PROJECT, { id: projectId });
      can(Actions.MANAGE, Subjects.PROJECT_USER, { projectId });
      // Cashflow (all Restricted for PM)
      // Note: overheads are only updated at the site level; updating a site's overheads
      // automatically propagates to the related project cashflow, so UPDATE is on SITE_CASHFLOW only.
      can([Actions.READ, Actions.EXPORT], Subjects.PROJECT_CASHFLOW, { projectId });
      // Site user management — scoped to the projects this manager runs (PLE-1285).
      can(Actions.MANAGE, Subjects.SITE_USER, { projectId });
      can([Actions.READ, Actions.UPDATE, Actions.EXPORT], Subjects.SITE_CASHFLOW, { projectId });
      // Bank draws — runs the project, so full on its draws and on the schedule they hang off.
      can([Actions.READ, Actions.LIST, Actions.UPDATE], Subjects.BANK_DRAW, { projectId });
      can([Actions.READ, Actions.CREATE], Subjects.CLAIM_SHEET, { projectId });
      can(Actions.UPDATE, Subjects.DRAW_SCHEDULE, { projectId });
      // Budget summary card (Projects section "View Project Budget Summaries" = Restricted for PM)
      can(Actions.READ, Subjects.PROJECT_BUDGET_SUMMARY_CARD, { projectId });
      // BOQ (full management within project scope)
      can(Actions.MANAGE, Subjects.BOQ, { projectId });
      can(Actions.READ, Subjects.SELLING_BOQ, { projectId });
      can(Actions.CREATE, Subjects.BOQ_TEMPLATE, { projectId });
      can(Actions.MANAGE, Subjects.BOQ_ITEM, { projectId });
      can(Actions.MANAGE, Subjects.BOQ_VARIATION, { projectId });
      // No APPROVE: the project manager decides only an amount increase, which arrives in M3.
      can([Actions.READ, Actions.CREATE], Subjects.PROVISIONAL_BUDGET, { projectId });
      // Client Variations
      can(
        [Actions.READ, Actions.LIST, Actions.CREATE, Actions.UPDATE, Actions.CANCEL],
        Subjects.CLIENT_VARIATION,
        { projectId },
      );
      can(Actions.CREATE, Subjects.CLIENT_VARIATION_PAYMENT, { projectId });
      // Orders (all Restricted for PM)
      can(Actions.MANAGE, Subjects.CART, { projectId });
      can(Actions.MANAGE, Subjects.ORDER, { projectId });
      // Context: read only, and never CREATE — FR-45 restricts *initiation* to PROJ_PROC_OFF,
      // but says nothing about visibility, and a project manager who can see every ordinary
      // order in their project has no reason to be blind to a bulk one spanning it. Scoped to
      // the project rather than org-wide, unlike ORDER's list arm (PE-608 contract, PR #2124).
      // APPROVE is the bulk approve arm (DL-154, A-15): a project manager approves an ordinary
      // order in the project, so approves a bulk one spanning it. DECLINE, CANCEL, LOG_DELIVERY
      // and COMPLETE take the same grant (PE-1033, PE-1049, PE-1054), as do SHARE and UPDATE (DL-260).
      can(
        [
          Actions.READ,
          Actions.LIST,
          Actions.APPROVE,
          Actions.DECLINE,
          Actions.CANCEL,
          Actions.LOG_DELIVERY,
          Actions.COMPLETE,
          Actions.SHARE,
          Actions.UPDATE,
        ],
        Subjects.BULK_ORDER,
        { projectId },
      );
      can(Actions.MANAGE, Subjects.ORDER_VARIATION, { projectId });
      can([Actions.UPLOAD, Actions.DELETE], Subjects.ORDER_DOCUMENTATION, { projectId });
      can(Actions.CREATE, Subjects.ORDER_DELIVERY, { projectId });
      // Payment Requests — read only. The API admits PROJ_MNGR to neither review stage:
      // CanVerify is SITE_FIN_OFF alone and CanFinalApprove is ORG_FO alone, so the
      // approve/reject grant here was a dead click the UI never offered anyway (PLE-1376). No
      // CREATE: PRs are generated on approval, never made by hand.
      can([Actions.READ, Actions.LIST], Subjects.PAYMENT_REQUEST, { projectId });
      can([Actions.UPLOAD, Actions.DELETE], Subjects.PR_DOCUMENTATION, { projectId });
      // Compliance (view + upload/delete/download documents)
      can(Actions.READ, Subjects.COMPLIANCE, { projectId });
      can([Actions.UPLOAD, Actions.DELETE, Actions.DOWNLOAD], Subjects.COMPLIANCE_DOCUMENT, {
        projectId,
      });
      // Returns (full management — create, read, list, approve, decline, cancel)
      can(
        [
          Actions.CREATE,
          Actions.READ,
          Actions.LIST,
          Actions.APPROVE,
          Actions.DECLINE,
          Actions.CANCEL,
        ],
        Subjects.RETURN,
        { projectId },
      );
      // Credits & Refunds — PM is view-only (no CANCEL, no COMPLETE) (PE-213 / V2 sheet).
      const pmCreditActions = [Actions.READ, Actions.LIST];
      can(pmCreditActions, Subjects.CREDIT, { projectId });
      can(pmCreditActions, Subjects.REFUND, { projectId });
    }
  }

  grantProjectProcurementOfficer(can, projectsByRole);

  if (projectsByRole.has(ProjRoles.PROJ_PROF)) {
    const projectIds = projectsByRole.get(ProjRoles.PROJ_PROF)!;

    // --- Unconditional grants ---
    can([Actions.READ, Actions.LIST], Subjects.ORGANISATION);
    // PP cannot view organisation users
    can(Actions.READ, Subjects.CLIENT_DASHBOARD);
    // Bank draws — a professional sees the draw schedule but never generates a claim, so no
    // CLAIM_SHEET grant (PLE-1236 A5).

    // --- Per-project conditional grants ---
    // PP cannot view Cost BOQ (only Selling BOQ/budget summary/variations), nor its provisional
    // budgets, which are Cost-BOQ-internal (FR-DS4)
    // PP cannot edit Client Variations, but can approve/decline
    for (const projectId of projectIds) {
      can([Actions.READ, Actions.LIST], Subjects.PROJECT, { id: projectId });
      can([Actions.READ, Actions.LIST], Subjects.SITE, { projectId });
      // Bank draws — the schedule only, same as the site professional.
      can([Actions.READ, Actions.LIST], Subjects.BANK_DRAW, { projectId });
      can(Actions.READ, Subjects.SELLING_BOQ, { projectId }); // Selling BOQ, budget summary, internal variations
      // Sites section "View Project Budget Summary" = Restricted for PROJ_PROF (per-project)
      can(Actions.READ, Subjects.SITE_PROJECT_BUDGET_SUMMARY_TILES, { projectId });
      can(
        [Actions.READ, Actions.LIST, Actions.APPROVE, Actions.DECLINE],
        Subjects.CLIENT_VARIATION,
        { projectId },
      );
    }
  }

  if (projectsByRole.has(ProjRoles.PROJ_CLI_APRV) || projectsByRole.has(ProjRoles.PROJ_CLI_BASE)) {
    const projectIds = [
      ...(projectsByRole.get(ProjRoles.PROJ_CLI_APRV) ?? []),
      ...(projectsByRole.get(ProjRoles.PROJ_CLI_BASE) ?? []),
    ];

    // Navigation read
    can(Actions.READ, Subjects.CLIENT_DASHBOARD);

    // Per-project conditional resource grants
    for (const projectId of [...new Set(projectIds)]) {
      can([Actions.READ, Actions.LIST], Subjects.CLIENT_VARIATION, { projectId });
    }
  }

  if (projectsByRole.has(ProjRoles.PROJ_CLI_APRV)) {
    for (const projectId of projectsByRole.get(ProjRoles.PROJ_CLI_APRV)!) {
      can([Actions.APPROVE, Actions.DECLINE], Subjects.CLIENT_VARIATION, { projectId });
    }
  }
};

/**
 * Applies all site-role grants for every site the user has a role in.
 *
 * Same two-tier strategy as applyProjectRoles:
 * 1. Unconditional READ/nav grants — for UI visibility checks.
 * 2. Per-site conditional grants — for resource-level checks via subject():
 *      ability.can(Actions.MANAGE, subject(Subjects.BOQ, { siteId: 'abc' }))
 */
const applySiteRoles = (can: GrantFn, sites: UserRoleMapping[]): void => {
  const sitesByRole = new Map<SiteRoles, string[]>();
  for (const site of sites) {
    for (const role of site.roles as SiteRoles[]) {
      const existing = sitesByRole.get(role) ?? [];
      sitesByRole.set(role, [...existing, site.id]);
    }
  }

  if (sitesByRole.has(SiteRoles.SITE_ADMIN)) {
    const siteIds = sitesByRole.get(SiteRoles.SITE_ADMIN)!;

    // --- Unconditional grants ---
    can([Actions.READ, Actions.LIST], Subjects.ORGANISATION);
    can(Actions.READ, Subjects.ORG_USER);
    // Vendors — SA can list/create/delete org vendors; no UPDATE (SA=None for Edit Org Vendor)
    can([Actions.READ, Actions.LIST, Actions.CREATE, Actions.DELETE], Subjects.ORG_VENDOR);
    can([Actions.READ, Actions.LIST], Subjects.BOQ_VENDOR); // List/View BOQ Vendors = Full
    can(Actions.SYNC, Subjects.BOQ_VENDOR);
    can(
      [
        Actions.READ,
        Actions.LIST,
        Actions.CREATE,
        Actions.UPDATE,
        Actions.DELETE,
        Actions.BULK_UPLOAD,
      ],
      Subjects.ORG_VENDOR_ITEM,
    );
    can(Actions.READ, Subjects.BOQ_TEMPLATE);
    // Accounts Payable — SA has no AP access (Financial Permissions Cleanup, PE-213).
    // Payments — List Payments + View proof of payment = Full
    can([Actions.READ, Actions.LIST], Subjects.PAYMENT);
    // Compliance templates (view only, Full)
    can(Actions.READ, Subjects.COMPLIANCE_TEMPLATE);
    // Context: SITE_USER is granted per site below, not here. CanUpdate(siteId) admits
    // SITE_ADMIN only via AssignedToSite(siteId, [SITE_ADMIN]), so the unconditional grant let
    // a site admin manage members of any site in the org — refused by the API, and wider than
    // ManageSiteMembers.vue has ever been (PLE-1285).
    // Context: PROJECT stays unconditional by design — a site-role user carries no project
    // assignment, so there is nothing to scope by. Listing is org-wide per the Core API contract;
    // viewing a project is narrowed server-side to ones holding the user's sites.
    // SITE and ORDER used to sit alongside it "for the nav". They did not need to: a bare
    // can(READ, X) matches a conditional rule, so the per-site grants below already satisfy
    // every nav check, and the unconditional pair only broke instance scoping (PLE-1241).
    can([Actions.READ, Actions.LIST], Subjects.PROJECT);

    // --- Per-site conditional grants (Restricted in CSV for SA) ---
    for (const siteId of siteIds) {
      // Site management on their own site, archiving included: SitePermissionService has no
      // Delete action at all, so archive reaches the API as an Update (PLE-1317 Q5).
      can([Actions.READ, Actions.LIST, Actions.UPDATE], Subjects.SITE, { siteId });
      // Site user management — scoped to the sites this admin is assigned to (PLE-1285).
      can(Actions.MANAGE, Subjects.SITE_USER, { siteId });
      // Bank draws — read and generate claims, but NOT update. Owner ruling: a site admin does
      // not change the draw schedule; that stays with the org and the project-operational roles.
      can([Actions.READ, Actions.LIST], Subjects.BANK_DRAW, { siteId });
      can([Actions.READ, Actions.CREATE], Subjects.CLAIM_SHEET, { siteId });
      // Cashflow (view + export + update overheads)
      can([Actions.READ, Actions.EXPORT, Actions.UPDATE], Subjects.SITE_CASHFLOW, { siteId });
      // BOQ (create/read/delete/export/submit/approve/reject)
      can(
        [
          Actions.CREATE,
          Actions.READ,
          Actions.DELETE,
          Actions.EXPORT,
          Actions.SUBMIT,
          Actions.APPROVE,
          Actions.DECLINE,
        ],
        Subjects.BOQ,
        { siteId },
      );
      can(Actions.READ, Subjects.SELLING_BOQ, { siteId }); // selling BOQ, budget summary, internal variations
      can(Actions.CREATE, Subjects.BOQ_TEMPLATE, { siteId });
      can(Actions.MANAGE, Subjects.BOQ_ITEM, { siteId }); // sections, items, groups, claims
      can(Actions.CREATE, Subjects.BOQ_VARIATION, { siteId }); // can add but not approve/reject
      can([Actions.READ, Actions.CREATE], Subjects.PROVISIONAL_BUDGET, { siteId });
      // Client Variations (view + create/edit/cancel — SA cannot approve/deny)
      can(
        [Actions.READ, Actions.LIST, Actions.CREATE, Actions.UPDATE, Actions.CANCEL],
        Subjects.CLIENT_VARIATION,
        { siteId },
      );
      can(Actions.CREATE, Subjects.CLIENT_VARIATION_PAYMENT, { siteId });
      // Cart (view only — SA cannot add/edit/manage cart items)
      can(Actions.READ, Subjects.CART, { siteId });
      // Orders (view/export/share + log delivery — SA cannot create/approve/cancel)
      can([Actions.READ, Actions.LIST, Actions.EXPORT, Actions.SHARE], Subjects.ORDER, { siteId });
      can(Actions.CREATE, Subjects.ORDER_DELIVERY, { siteId });
      can([Actions.UPLOAD, Actions.DELETE], Subjects.ORDER_DOCUMENTATION, { siteId });
      // Payment Requests — view only. Neither review stage admits SITE_ADMIN (PLE-1376).
      can([Actions.READ, Actions.LIST], Subjects.PAYMENT_REQUEST, { siteId });
      // Compliance (view + upload/delete/download documents)
      can(Actions.READ, Subjects.COMPLIANCE, { siteId });
      can([Actions.UPLOAD, Actions.DELETE, Actions.DOWNLOAD], Subjects.COMPLIANCE_DOCUMENT, {
        siteId,
      });
      // Context: view only. ReturnPermissionService.CanCreate has no SITE_ADMIN arm, though
      // CanView and CanUpdate both do — raising a return is procurement plus finance, which
      // looks like a deliberate separation of duties (PLE-1277).
      can([Actions.READ, Actions.LIST], Subjects.RETURN, { siteId });
      // Credits & Refunds (view only — SA cannot cancel/complete)
      can([Actions.READ, Actions.LIST], Subjects.CREDIT, { siteId });
      can([Actions.READ, Actions.LIST], Subjects.REFUND, { siteId });
    }
  }

  if (sitesByRole.has(SiteRoles.SITE_PROC_OFF)) {
    const siteIds = sitesByRole.get(SiteRoles.SITE_PROC_OFF)!;

    // --- Unconditional grants (Full in CSV for SPO) ---
    can([Actions.READ, Actions.LIST], Subjects.ORGANISATION);
    can(Actions.READ, Subjects.ORG_USER);
    // Vendors — create/read/update org vendors; no DELETE (SPO=None for Delete Org Vendor)
    can([Actions.READ, Actions.LIST, Actions.CREATE, Actions.UPDATE], Subjects.ORG_VENDOR);
    can([Actions.READ, Actions.LIST], Subjects.BOQ_VENDOR);
    can(Actions.SYNC, Subjects.BOQ_VENDOR);
    // Org Items — full management including delete and bulk upload
    can(
      [
        Actions.READ,
        Actions.LIST,
        Actions.CREATE,
        Actions.UPDATE,
        Actions.DELETE,
        Actions.BULK_UPLOAD,
      ],
      Subjects.ORG_VENDOR_ITEM,
    );
    can(Actions.READ, Subjects.BOQ_TEMPLATE);
    // Accounts Payable — SPO has no AP access (Financial Permissions Cleanup, PE-213).
    // Payments — List only. SPO has no Create/Log Payment: logging a payment depends on the AP
    // list, which SPO does not have (PE-213), so the button would dead-click (PE-537). Adhoc
    // (ADV) creation mirrors CREATE:PAYMENT and is likewise not granted (PE-537).
    can([Actions.READ, Actions.LIST], Subjects.PAYMENT);
    // Compliance templates (view only, Full)
    can(Actions.READ, Subjects.COMPLIANCE_TEMPLATE);
    // Context: PROJECT stays unconditional by design — a site-role user carries no project
    // assignment, so there is nothing to scope by. Listing is org-wide per the Core API contract;
    // viewing a project is narrowed server-side to ones holding the user's sites.
    // SITE and ORDER used to sit alongside it "for the nav". They did not need to: a bare
    // can(READ, X) matches a conditional rule, so the per-site grants below already satisfy
    // every nav check, and the unconditional pair only broke instance scoping (PLE-1241).
    can([Actions.READ, Actions.LIST], Subjects.PROJECT);

    // --- Per-site conditional grants (Restricted in CSV for SPO) ---
    for (const siteId of siteIds) {
      // Site (view only — SPO cannot edit or delete sites)
      can([Actions.READ, Actions.LIST], Subjects.SITE, { siteId });
      // Bank draws — sees draws and generates claim sheets; site dates stay with the admin.
      can([Actions.READ, Actions.LIST], Subjects.BANK_DRAW, { siteId });
      can([Actions.READ, Actions.CREATE], Subjects.CLAIM_SHEET, { siteId });
      // Cashflow (view + export + update overheads)
      can([Actions.READ, Actions.EXPORT, Actions.UPDATE], Subjects.SITE_CASHFLOW, { siteId });
      // BOQ (full management incl. approve/reject — SPO has the most BOQ permissions of site roles)
      can(
        [
          Actions.CREATE,
          Actions.READ,
          Actions.UPDATE,
          Actions.DELETE,
          Actions.EXPORT,
          Actions.SUBMIT,
          Actions.APPROVE,
          Actions.DECLINE,
        ],
        Subjects.BOQ,
        { siteId },
      );
      can(Actions.READ, Subjects.SELLING_BOQ, { siteId });
      can(Actions.CREATE, Subjects.BOQ_TEMPLATE, { siteId });
      can(Actions.MANAGE, Subjects.BOQ_ITEM, { siteId });
      // BOQ Variations — add + approve/reject (SPO is the approver at site level)
      can([Actions.CREATE, Actions.APPROVE, Actions.DECLINE], Subjects.BOQ_VARIATION, { siteId });
      // The approver of a provisional-budget create request (FR-C2) and of an allocation
      // (DL-137). Each decide grant names its kind; a decide check without one is refused (DL-67).
      can([Actions.READ, Actions.CREATE], Subjects.PROVISIONAL_BUDGET, { siteId });
      for (const kind of PROCUREMENT_DECIDED_KINDS) {
        can([Actions.APPROVE, Actions.DECLINE], Subjects.PROVISIONAL_BUDGET, { siteId, kind });
      }
      // Client Variations (view + create/edit/cancel — SPO cannot approve/deny)
      can(
        [Actions.READ, Actions.LIST, Actions.CREATE, Actions.UPDATE, Actions.CANCEL],
        Subjects.CLIENT_VARIATION,
        { siteId },
      );
      can(Actions.CREATE, Subjects.CLIENT_VARIATION_PAYMENT, { siteId });
      // Cart (full management)
      can(Actions.MANAGE, Subjects.CART, { siteId });
      // Orders (full management — SPO can create, approve, cancel, log delivery)
      can(Actions.MANAGE, Subjects.ORDER, { siteId });
      can(Actions.MANAGE, Subjects.ORDER_VARIATION, { siteId });
      can([Actions.UPLOAD, Actions.DELETE], Subjects.ORDER_DOCUMENTATION, { siteId });
      can(Actions.CREATE, Subjects.ORDER_DELIVERY, { siteId });
      // Payment Requests — view only. Neither review stage admits SITE_PROC_OFF (PLE-1376), and
      // PRs are generated on approval, never made by hand.
      can([Actions.READ, Actions.LIST], Subjects.PAYMENT_REQUEST, { siteId });
      can([Actions.UPLOAD, Actions.DELETE], Subjects.PR_DOCUMENTATION, { siteId });
      // Compliance (view + download only — SPO cannot upload/delete docs)
      can(Actions.READ, Subjects.COMPLIANCE, { siteId });
      can(Actions.DOWNLOAD, Subjects.COMPLIANCE_DOCUMENT, { siteId });
      // Returns (full management — create, read, list, approve, decline, cancel)
      can(
        [
          Actions.CREATE,
          Actions.READ,
          Actions.LIST,
          Actions.APPROVE,
          Actions.DECLINE,
          Actions.CANCEL,
        ],
        Subjects.RETURN,
        { siteId },
      );
      // Credits & Refunds (view only — SPO cannot cancel or complete)
      can([Actions.READ, Actions.LIST], Subjects.CREDIT, { siteId });
      can([Actions.READ, Actions.LIST], Subjects.REFUND, { siteId });
    }
  }

  if (sitesByRole.has(SiteRoles.SITE_FIN_OFF)) {
    const siteIds = sitesByRole.get(SiteRoles.SITE_FIN_OFF)!;

    // --- Unconditional grants ---
    can([Actions.READ, Actions.LIST], Subjects.ORGANISATION);
    can(Actions.READ, Subjects.ORG_USER);
    // Vendors — read/list only (no create/edit/delete)
    can([Actions.READ, Actions.LIST], Subjects.ORG_VENDOR);
    can([Actions.READ, Actions.LIST], Subjects.BOQ_VENDOR);
    can([Actions.READ, Actions.LIST], Subjects.ORG_VENDOR_ITEM);
    can(Actions.READ, Subjects.BOQ_TEMPLATE);
    // Accounts Payable — SFO has no AP access (Financial Permissions Cleanup, PE-213).
    // Payments — List Payments + View proof of payment only. SFO has no Create/Log Payment:
    // logging a payment depends on the AP list, which SFO does not have (PE-213), so the button
    // would dead-click (PE-537). Adhoc (ADV) creation mirrors CREATE:PAYMENT and is likewise not
    // granted (PE-537).
    can([Actions.READ, Actions.LIST], Subjects.PAYMENT);
    // Context: AP Statement is org-finance only — see the note in applyProjectRoles. SFO holds
    // no AP list either (PE-213), so there was nothing to generate a statement from (PLE-1275).
    // Compliance templates (view only, Full)
    can(Actions.READ, Subjects.COMPLIANCE_TEMPLATE);
    // Context: PROJECT stays unconditional by design — a site-role user carries no project
    // assignment, so there is nothing to scope by. Listing is org-wide per the Core API contract;
    // viewing a project is narrowed server-side to ones holding the user's sites.
    // SITE and ORDER used to sit alongside it "for the nav". They did not need to: a bare
    // can(READ, X) matches a conditional rule, so the per-site grants below already satisfy
    // every nav check, and the unconditional pair only broke instance scoping (PLE-1241).
    can([Actions.READ, Actions.LIST], Subjects.PROJECT);

    // --- Per-site conditional grants ---
    for (const siteId of siteIds) {
      // Site (view only — SFO cannot edit or delete sites)
      can([Actions.READ, Actions.LIST], Subjects.SITE, { siteId });
      // Bank draws — finance-shaped work on a finance screen; added with PLE-1236 A3.
      can([Actions.READ, Actions.LIST], Subjects.BANK_DRAW, { siteId });
      can([Actions.READ, Actions.CREATE], Subjects.CLAIM_SHEET, { siteId });
      // Cashflow (view + export + update overheads)
      can([Actions.READ, Actions.EXPORT, Actions.UPDATE], Subjects.SITE_CASHFLOW, { siteId });
      // BOQ (view + approve/reject — SFO cannot create/delete/submit BOQs)
      can([Actions.READ, Actions.APPROVE, Actions.DECLINE], Subjects.BOQ, { siteId });
      can(Actions.READ, Subjects.SELLING_BOQ, { siteId }); // selling BOQ, budget summary, internal variations
      can(Actions.READ, Subjects.PROVISIONAL_BUDGET, { siteId });
      // Client Variations (view + log payment — SFO cannot create/edit/cancel/approve)
      can([Actions.READ, Actions.LIST], Subjects.CLIENT_VARIATION, { siteId });
      can(Actions.CREATE, Subjects.CLIENT_VARIATION_PAYMENT, { siteId });
      // Cart (view only — SFO cannot add/edit/manage cart)
      can(Actions.READ, Subjects.CART, { siteId });
      // Orders (view/export/share + upload docs + log delivery — SFO cannot create/approve/cancel)
      can([Actions.READ, Actions.LIST, Actions.EXPORT, Actions.SHARE], Subjects.ORDER, { siteId });
      can(Actions.CREATE, Subjects.ORDER_DELIVERY, { siteId });
      // Context: upload and remove share one audience. Both paths run
      // ValidateDocumentUpload / RemoveDocument, which validate EntityAction.Update on the order,
      // and OrderPermissionService.CanUpdate admits SITE_FIN_OFF — the upload-only split had no
      // API counterpart (PLE-1236 D10).
      can([Actions.UPLOAD, Actions.DELETE], Subjects.ORDER_DOCUMENTATION, { siteId });
      // Payment Requests — Stage 1. VERIFY is SFO's alone; APPROVE (Stage 2) belongs to ORG_FO.
      //
      // Context: UPDATE is added here because PaymentRequestPermissionService.CanUpdate admits
      // SITE_FIN_OFF explicitly, and the payment-details form has always relied on it. The
      // factory omitting it is what would have taken payment-field editing away from the site
      // finance officer at flag-flip — the same control, and the same role, as the incident
      // #2275 fixed (PLE-1376).
      can([Actions.READ, Actions.LIST, Actions.VERIFY, Actions.UPDATE], Subjects.PAYMENT_REQUEST, {
        siteId,
      });
      can([Actions.UPLOAD, Actions.DELETE], Subjects.PR_DOCUMENTATION, { siteId });
      // Compliance (view + upload/delete/download documents)
      can(Actions.READ, Subjects.COMPLIANCE, { siteId });
      can([Actions.UPLOAD, Actions.DELETE, Actions.DOWNLOAD], Subjects.COMPLIANCE_DOCUMENT, {
        siteId,
      });
      // Returns (view only — SFO cannot create/approve/cancel returns)
      // Context: SFO may raise a return — ReturnPermissionService.CanCreate lists SITE_FIN_OFF
      // alongside SITE_PROC_OFF. The frontend had hidden it (PLE-1277).
      can([Actions.CREATE, Actions.READ, Actions.LIST], Subjects.RETURN, { siteId });
      // Credits & Refunds — SFO has no CANCEL but keeps COMPLETE (PE-213 / V2 sheet).
      const sfoCreditActions = [Actions.READ, Actions.LIST, Actions.COMPLETE];
      can(sfoCreditActions, Subjects.CREDIT, { siteId });
      can(sfoCreditActions, Subjects.REFUND, { siteId });
    }
  }

  if (sitesByRole.has(SiteRoles.SITE_FLD_USR)) {
    const siteIds = sitesByRole.get(SiteRoles.SITE_FLD_USR)!;

    // --- Unconditional grants (Full in CSV for SFU) ---
    can([Actions.READ, Actions.LIST], Subjects.ORGANISATION);
    can(Actions.READ, Subjects.ORG_USER);
    // Vendors — read/list org & BOQ vendors; can edit org items but not create/delete/bulk-upload
    can([Actions.READ, Actions.LIST], Subjects.ORG_VENDOR);
    can([Actions.READ, Actions.LIST], Subjects.BOQ_VENDOR);
    can([Actions.READ, Actions.LIST, Actions.UPDATE], Subjects.ORG_VENDOR_ITEM);
    can(Actions.READ, Subjects.BOQ_TEMPLATE);
    // Compliance templates (view only, Full)
    can(Actions.READ, Subjects.COMPLIANCE_TEMPLATE);
    // Payments — Site Worker has no Payment view (PE-460, mirroring BE PE-461 — SFU is not a
    // finance role).
    // Context: PROJECT stays unconditional by design — a site-role user carries no project
    // assignment, so there is nothing to scope by. Listing is org-wide per the Core API contract;
    // viewing a project is narrowed server-side to ones holding the user's sites.
    // SITE and ORDER used to sit alongside it "for the nav". They did not need to: a bare
    // can(READ, X) matches a conditional rule, so the per-site grants below already satisfy
    // every nav check, and the unconditional pair only broke instance scoping (PLE-1241).
    can([Actions.READ, Actions.LIST], Subjects.PROJECT);

    // --- Per-site conditional grants (Restricted in CSV for SFU) ---
    for (const siteId of siteIds) {
      // Site (view only — SFU cannot edit/delete sites)
      can([Actions.READ, Actions.LIST], Subjects.SITE, { siteId });
      // Cashflow (view + export + update overheads)
      can([Actions.READ, Actions.EXPORT, Actions.UPDATE], Subjects.SITE_CASHFLOW, { siteId });
      // BOQ (create/read/delete/export/submit — SFU cannot approve/reject BOQs)
      can(
        [Actions.CREATE, Actions.READ, Actions.DELETE, Actions.EXPORT, Actions.SUBMIT],
        Subjects.BOQ,
        { siteId },
      );
      can(Actions.READ, Subjects.SELLING_BOQ, { siteId }); // selling BOQ, budget summary, internal variations
      can(Actions.CREATE, Subjects.BOQ_TEMPLATE, { siteId });
      can(Actions.MANAGE, Subjects.BOQ_ITEM, { siteId }); // sections, items, groups, claims
      can(Actions.CREATE, Subjects.BOQ_VARIATION, { siteId }); // can add but not approve/reject
      can([Actions.READ, Actions.CREATE], Subjects.PROVISIONAL_BUDGET, { siteId });
      // Client Variations (view only — SFU cannot create/edit/cancel/approve)
      can([Actions.READ, Actions.LIST], Subjects.CLIENT_VARIATION, { siteId });
      // Cart (full management — SFU can add/edit/clear/remove cart items)
      can(Actions.MANAGE, Subjects.CART, { siteId });
      // Orders (create + view/export/share + docs + delivery — SFU cannot approve/cancel)
      can(
        [Actions.CREATE, Actions.READ, Actions.LIST, Actions.EXPORT, Actions.SHARE],
        Subjects.ORDER,
        { siteId },
      );
      can(Actions.CREATE, Subjects.ORDER_DELIVERY, { siteId });
      can([Actions.UPLOAD, Actions.DELETE], Subjects.ORDER_DOCUMENTATION, { siteId });
      // Payment Requests — Site Worker has no PR visibility (PE-460, mirroring BE PE-461).
      // Compliance (view site compliance only — SFU cannot upload/download documents)
      can(Actions.READ, Subjects.COMPLIANCE, { siteId });
      // Returns (view only — SFU cannot create/approve/cancel returns)
      can([Actions.READ, Actions.LIST], Subjects.RETURN, { siteId });
    }
  }

  if (sitesByRole.has(SiteRoles.SITE_PROF)) {
    const siteIds = sitesByRole.get(SiteRoles.SITE_PROF)!;

    // --- Unconditional grants (Full in CSV for SP) ---
    can([Actions.READ, Actions.LIST], Subjects.ORGANISATION);
    // SP cannot view organisation users
    // SP has no access to vendors, org items, or BOQ templates
    can(Actions.READ, Subjects.CLIENT_DASHBOARD); // View Client Dashboard = Full
    can(Actions.READ, Subjects.SITE_PROJECT_BUDGET_SUMMARY_TILES); // Sites section "View Project Budget Summary" = Full
    // Context: PROJECT stays unconditional by design — a site-role user carries no project
    // assignment, so there is nothing to scope by. Listing is org-wide per the Core API contract;
    // viewing a project is narrowed server-side to ones holding the user's sites.
    // SITE and ORDER used to sit alongside it "for the nav". They did not need to: a bare
    // can(READ, X) matches a conditional rule, so the per-site grants below already satisfy
    // every nav check, and the unconditional pair only broke instance scoping (PLE-1241).
    can([Actions.READ, Actions.LIST], Subjects.PROJECT);
    // SP has no additional navigation items beyond the above

    // --- Per-site conditional grants (Restricted in CSV for SP) ---
    // SP cannot view Cost BOQ — only Selling BOQ (budget summary + internal variations)
    // Nor its provisional budgets, which are Cost-BOQ-internal (FR-DS4)
    for (const siteId of siteIds) {
      can([Actions.READ, Actions.LIST], Subjects.SITE, { siteId });
      // Bank draws — the schedule only. A professional never generates a claim (PLE-1236 A5).
      can([Actions.READ, Actions.LIST], Subjects.BANK_DRAW, { siteId });
      can(Actions.READ, Subjects.SELLING_BOQ, { siteId });
      // Client Variations — SP is the professional approver; can view + approve/decline
      can(
        [Actions.READ, Actions.LIST, Actions.APPROVE, Actions.DECLINE],
        Subjects.CLIENT_VARIATION,
        { siteId },
      );
    }
  }

  if (sitesByRole.has(SiteRoles.SITE_CLI_APRV) || sitesByRole.has(SiteRoles.SITE_CLI_BASE)) {
    const siteIds = [
      ...(sitesByRole.get(SiteRoles.SITE_CLI_APRV) ?? []),
      ...(sitesByRole.get(SiteRoles.SITE_CLI_BASE) ?? []),
    ];

    can(Actions.READ, Subjects.CLIENT_DASHBOARD);

    for (const siteId of [...new Set(siteIds)]) {
      can([Actions.READ, Actions.LIST], Subjects.CLIENT_VARIATION, { siteId });
    }
  }

  if (sitesByRole.has(SiteRoles.SITE_CLI_APRV)) {
    for (const siteId of sitesByRole.get(SiteRoles.SITE_CLI_APRV)!) {
      can([Actions.APPROVE, Actions.DECLINE], Subjects.CLIENT_VARIATION, { siteId });
    }
  }
};

// -------------------------------------------------------------------------
// VendorControl role grant helpers
// -------------------------------------------------------------------------

/**
 * VC_SUPER — unrestricted access to all VendorControl subjects.
 */
const grantVCSuper = (can: GrantFn): void => {
  can(Actions.MANAGE, Subjects.VC_VENDOR);
  can(Actions.MANAGE, Subjects.VC_ITEM);
  can(Actions.MANAGE, Subjects.VC_INGESTION);
  can(Actions.MANAGE, Subjects.VC_PRICING);
  can(Actions.MANAGE, Subjects.VC_COMPLIANCE);
  can(Actions.MANAGE, Subjects.VC_STAGING);
  can(Actions.MANAGE, Subjects.VC_AUDIT_LOG);
  can(Actions.MANAGE, Subjects.VC_SETTINGS);
};

/**
 * VC_DATA_STEWARD — manages vendor catalogue data and ingestion pipelines.
 * CRUD on Vendor, Item, Ingestion; can create Compliance records; read-only on AuditLog and Settings.
 */
const grantVCDataSteward = (can: GrantFn): void => {
  can([Actions.CREATE, Actions.READ, Actions.UPDATE, Actions.DELETE], Subjects.VC_VENDOR);
  can([Actions.CREATE, Actions.READ, Actions.UPDATE, Actions.DELETE], Subjects.VC_ITEM);
  can([Actions.CREATE, Actions.READ, Actions.UPDATE, Actions.DELETE], Subjects.VC_INGESTION);
  can(Actions.CREATE, Subjects.VC_COMPLIANCE);
  can(Actions.READ, Subjects.VC_AUDIT_LOG);
  can(Actions.READ, Subjects.VC_SETTINGS);
};

/**
 * VC_PRICING_ADMIN — owns pricing rules and markup configuration.
 * Full management of Pricing and Settings; read-only on Vendor and Item.
 */
const grantVCPricingAdmin = (can: GrantFn): void => {
  can(Actions.MANAGE, Subjects.VC_PRICING);
  can([Actions.READ, Actions.LIST], Subjects.VC_VENDOR);
  can([Actions.READ, Actions.LIST], Subjects.VC_ITEM);
  can(Actions.MANAGE, Subjects.VC_SETTINGS);
};

/**
 * VC_STORE_MANAGER — manages staging and item visibility for store operations.
 * Full management of Staging; can update Items; read-only on Vendor, Item, Pricing, AuditLog.
 */
const grantVCStoreManager = (can: GrantFn): void => {
  can(Actions.MANAGE, Subjects.VC_STAGING);
  can(Actions.UPDATE, Subjects.VC_ITEM);
  can([Actions.READ, Actions.LIST], Subjects.VC_VENDOR);
  can([Actions.READ, Actions.LIST], Subjects.VC_ITEM);
  can(Actions.READ, Subjects.VC_PRICING);
  can(Actions.READ, Subjects.VC_AUDIT_LOG);
};

/**
 * VC_COMPLIANCE_OFFICER — owns compliance workflows, document types and audit records.
 * Full management of Compliance, Settings, and AuditLog; read-only on Vendor and Item.
 */
const grantVCComplianceOfficer = (can: GrantFn): void => {
  can(Actions.MANAGE, Subjects.VC_COMPLIANCE);
  can(Actions.MANAGE, Subjects.VC_SETTINGS);
  can([Actions.READ, Actions.LIST], Subjects.VC_VENDOR);
  can([Actions.READ, Actions.LIST], Subjects.VC_ITEM);
  can(Actions.MANAGE, Subjects.VC_AUDIT_LOG);
};

// -------------------------------------------------------------------------
// Main builder — orchestrates which grants apply for the current user context
// -------------------------------------------------------------------------

export const buildAbility = (user: User, context: PermissionContext): AppAbility => {
  const { can, build } = new AbilityBuilder<AppAbility>(PureAbility);

  // A user may have multiple entries for the same organisation when role groups
  // come from separate sources (e.g. ERP roles and Vendor Collaboration roles
  // arriving as distinct memberships). Merge every matching entry so the full
  // set of grants for the active org is applied, not just the first match.
  const orgEntries = user.roles.organisations.filter((o) => o.id === context.activeOrgId);

  // BOQ platform roles (BOQ_ADMIN / BOQ_OWN) are cross-organisation: they live in the
  // user-wide `roles.boq` list and govern in EVERY org context, regardless of whether the
  // active org is one the user holds an org-level role in. This mirrors the backend, whose
  // BOQ-role lookup is user-wide and short-circuits org roles. Sourcing them from
  // `roles.boq` (with the per-org roles as a defensive fallback) is what keeps a platform
  // admin authorised after a refresh that rebuilds the ability against the URL's org.
  const boqRoles = new Set(user.roles.boq);
  const allRoles = new Set(orgEntries.flatMap((o) => o.roles));
  const isBoqAdmin = boqRoles.has(BOQRoles.BOQ_ADMIN) || allRoles.has(BOQRoles.BOQ_ADMIN);
  const isBoqOwner = boqRoles.has(BOQRoles.BOQ_OWN) || allRoles.has(BOQRoles.BOQ_OWN);
  const isCatalogAdmin =
    boqRoles.has(BOQRoles.BOQ_CAT_ADMIN) || allRoles.has(BOQRoles.BOQ_CAT_ADMIN);
  const isMemberOfActiveOrg = orgEntries.length > 0;

  if (isCatalogAdmin) {
    grantCatalogAdmin(can);
  }

  // Nothing more to grant when the active org isn't one the user belongs to AND they hold
  // no cross-org BOQ platform role. A BOQ platform admin still resolves for any org; a
  // catalog admin keeps only the central catalogue grants above.
  if (!isMemberOfActiveOrg && !isBoqAdmin && !isBoqOwner) {
    return build({ conditionsMatcher: conditionsMatcher as never });
  }

  const projectMappings = orgEntries.flatMap((o) => o.projects ?? []);
  const siteMappings = orgEntries.flatMap((o) => o.sites ?? []);

  const isOrgAdmin = allRoles.has(OrgRoles.ORG_ADMIN);
  const isOrgOwner = allRoles.has(OrgRoles.ORG_OWN);
  const isOrgFO = allRoles.has(OrgRoles.ORG_FO);
  const isOrgFA = allRoles.has(OrgRoles.ORG_FA);

  if (isOrgAdmin || isOrgOwner || isBoqAdmin || isBoqOwner) {
    grantOrgOwner(can);
  }
  if (isOrgAdmin || isBoqAdmin) {
    grantOrgAdmin(can);
  }
  if (isOrgAdmin) {
    grantOrgAdminOnly(can);
  }
  if (isBoqAdmin) {
    grantBoqAdminExtras(can);
  }
  if (isBoqOwner) {
    grantBoqOwner(can);
  }
  // ORG_FO is a superset of ORG_FA in every grant but verifying and declining a Requested bulk
  // payment request (see grantOrgFABulkPaymentRequest). When a user holds both — a valid
  // combination, since any mix of org roles is allowed (PE-227) — ORG_FO is applied with those
  // grants added, so grantOrgFinanceBase() never runs twice.
  if (isOrgFO) {
    grantOrgFO(can);
    if (isOrgFA) grantOrgFABulkPaymentRequest(can);
  } else if (isOrgFA) {
    grantOrgFA(can);
  }

  applyProjectRoles(can, projectMappings);
  applySiteRoles(can, siteMappings);

  if (allRoles.has(VCRoles.VC_SUPER)) grantVCSuper(can);
  if (allRoles.has(VCRoles.VC_DATA_STEWARD)) grantVCDataSteward(can);
  if (allRoles.has(VCRoles.VC_PRICING_ADMIN)) grantVCPricingAdmin(can);
  if (allRoles.has(VCRoles.VC_STORE_MANAGER)) grantVCStoreManager(can);
  if (allRoles.has(VCRoles.VC_COMPLIANCE_OFFICER)) grantVCComplianceOfficer(can);

  return build({ conditionsMatcher: conditionsMatcher as never });
};
